import React from 'react';
import { Hospital, EditFormData, InsuranceItem } from '../../types/hospital.types';
import EditableField from './EditableField';

interface HospitalDetailsProps {
  hospital: Hospital;
  isEditing: boolean;
  editFormData: EditFormData;
  onInputChange: (field: keyof EditFormData, value: string | boolean | InsuranceItem[]) => void;
  onMapOpen: () => void;
}

const HospitalDetails: React.FC<HospitalDetailsProps> = ({
  hospital,
  isEditing,
  editFormData,
  onInputChange,
  onMapOpen
}) => {
  const booleanOptions = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' }
  ];

  return (
    <div className="hospital-details">
      <div className="details-sections">
        {/* Basic Information */}
        <div className="detail-section">
          <h4>Basic Information</h4>

          <EditableField
            label="Hospital Name"
            value={hospital.name}
            editValue={editFormData.name}
            isEditing={isEditing}
            onChange={(value) => onInputChange('name', value as string)}
          />

          <EditableField
            label="Full Address"
            value={hospital.address}
            editValue={editFormData.address}
            isEditing={isEditing}
            type="textarea"
            onChange={(value) => onInputChange('address', value as string)}
          />

          <div className="field-row">
            <label>Coordinates:</label>
            <div className="coordinates-container">
              {isEditing ? (
                <input
                  type="text"
                  value={editFormData.coordinates || ''}
                  onChange={(e) => onInputChange('coordinates', e.target.value)}
                  className="edit-input"
                  onClick={(e) => e.stopPropagation()}
                  placeholder="longitude, latitude"
                />
              ) : (
                <span>{hospital.location.coordinates.join(', ')}</span>
              )}
              <button
                className="btn-map"
                onClick={(e) => {
                  e.stopPropagation();
                  onMapOpen();
                }}
              >
                🗺️ Map
              </button>
            </div>
          </div>

          <div className="field-row">
            <label>Created:</label>
            <span>{new Date(hospital.createdAt).toLocaleDateString()}</span>
          </div>

          <div className="field-row">
            <label>Last Updated:</label>
            <span>{new Date(hospital.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Location Details */}
        <div className="detail-section">
          <h4>Location Details</h4>

          <EditableField
            label="Address"
            value={hospital.formattedData.address}
            editValue={editFormData['formattedData.address']}
            isEditing={isEditing}
            onChange={(value) => onInputChange('formattedData.address', value as string)}
          />

          <EditableField
            label="City"
            value={hospital.formattedData.city}
            editValue={editFormData['formattedData.city']}
            isEditing={isEditing}
            onChange={(value) => onInputChange('formattedData.city', value as string)}
          />

          <EditableField
            label="District"
            value={hospital.formattedData.district}
            editValue={editFormData['formattedData.district']}
            isEditing={isEditing}
            onChange={(value) => onInputChange('formattedData.district', value as string)}
          />

          <EditableField
            label="State"
            value={hospital.formattedData.state}
            editValue={editFormData['formattedData.state']}
            isEditing={isEditing}
            onChange={(value) => onInputChange('formattedData.state', value as string)}
          />

          <EditableField
            label="Pincode"
            value={hospital.formattedData.pincode}
            editValue={editFormData['formattedData.pincode']}
            isEditing={isEditing}
            onChange={(value) => onInputChange('formattedData.pincode', value as string)}
          />
        </div>

        {/* Services */}
        <div className="detail-section">
          <h4>Services</h4>

          <EditableField
            label="ESIC"
            value={hospital.isEsic ? 'Yes' : 'No'}
            editValue={editFormData.isEsic}
            isEditing={isEditing}
            type="select"
            options={booleanOptions}
            onChange={(value) => onInputChange('isEsic', value as boolean)}
          />

          <EditableField
            label="Handle Emergency"
            value={hospital.handleEmergency ? 'Yes' : 'No'}
            editValue={editFormData.handleEmergency}
            isEditing={isEditing}
            type="select"
            options={booleanOptions}
            onChange={(value) => onInputChange('handleEmergency', value as boolean)}
          />

          <EditableField
            label="Specialities"
            value={hospital.speciality.length > 0 ? hospital.speciality.join(', ') : 'None'}
            editValue={editFormData.speciality}
            isEditing={isEditing}
            placeholder="Comma separated specialities"
            onChange={(value) => onInputChange('speciality', value as string)}
          />

          <EditableField
            label="Insurance List"
            value={hospital.insuranceList.length > 0 ? Array.from(new Set(hospital.insuranceList.map(item => item.nameOfInsurer))).join(', ') : 'None'}
            editValue={editFormData.insuranceList}
            isEditing={false}
            // type="insurance"
            placeholder="Comma separated insurance companies"
            onChange={(value) => onInputChange('insuranceList', value as InsuranceItem[])}
          />
        </div>
      </div>
    </div>
  );
};

export default HospitalDetails;